import React, { useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { Formik } from "formik";
import _ from "lodash";
import { RootState } from "us.helper/types";
import {
  UploadOutlined,
  DeleteOutlined,
  PlusOutlined,
  PaperClipOutlined,
  ExclamationCircleFilled,
} from "us.icons";
import { workflowAction } from "us.collection/actions";
import {
  $Message,
  $Popconfirm,
  $Select,
  $Row,
  $Col,
  $Tooltip,
  $Switch,
  $Popover,
  $Upload,
  $Button,
} from "us.common/components";
import { UploadActionTypes, FileConfig } from "./Constants";
import {
  WorkflowView,
  WorkflowDrawerType,
  StateType,
  workflowDrawerInitial,
} from "../../Constants";
import { ImportFiles } from "./Repository";
import { ImportWorkflowProps } from "./Interfaces";
import { getWorkflowCategoryOptions, resultHasErrors } from "./Functions";
import { ImportWorkflowValidationSchema } from "./Validations";
import { WorkflowGroup } from "us.collection/repository";
import { getEntityNumberAndType } from "us.collection/components/BMD/Functions";
import { matchPath, useLocation } from "react-router-dom";
import JSZip from "jszip";

const { Dragger } = $Upload;

/**
 * @description - Component for import states and events workflows
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/3055714493/Manage+State+Categories
 * @param {PropsFromRedux} props
 * @return {JSX.Element}
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @since 2/10/2024
 */
const ImportWorkflow: React.FC<ImportWorkflowProps & PropsFromRedux> = (
  props
) => {
  const { t } = useTranslation();
  const {
    metaData,
    importDrawer,
    uploadedResult,
    uploadedFiles,
    workflowStates,
    newCategoryId,
    workflowCategories,
    isCategorySaveing,
    isWorkflowsImporting,
    onClose,
    openDrawer,
    resetImport,
    updateFiles,
    importWorkflow,
    getCategories,
  } = props;

  const { caseId } = metaData.data ?? {};

  const formikRef = useRef<any>();
  const isResultAvailable = uploadedResult?.length > 0;
  const isStateWorkflowView = importDrawer?.workflowView == WorkflowView.STATES;

  const location = useLocation();
  const { params }: any = matchPath(
    {
      path: "/:type/:id?/:module?",
      end: false,
      caseSensitive: false,
    },
    location.pathname
  );

  useEffect(() => {
    if (!isStateWorkflowView) {
      getCategories && getCategories({});
    }
  }, []);

  useEffect(() => {
    if (newCategoryId > 0) {
      formikRef.current?.setFieldValue("category", newCategoryId);
    }
  }, [newCategoryId]);

  const isImportDisabled = useMemo(() => {
    return (
      !isResultAvailable ||
      uploadedFiles?.length == 0 ||
      resultHasErrors(uploadedResult) ||
      isWorkflowsImporting ||
      isCategorySaveing
    );
  }, [
    isResultAvailable,
    uploadedFiles,
    uploadedResult,
    isWorkflowsImporting,
    isCategorySaveing,
  ]);

  /**
   * @description - Update Uploaded file list
   */
  const updateFileList = (fileList: any) => {
    updateFiles && updateFiles(fileList);
  };

  /**
   * @description - Handles file upload process
   */
  const beforeUploadFile = async (file: any, list: any) => {
    let newList: any = list;
    if (file.size / 1024 / 1024 > FileConfig.maxSize) {
      $Message.error(t("US.COMMON:COMMON.FILE_SIZE_EXCEEDED"));
    } else {
      if (file.type === "application/zip" || file.name.endsWith(".zip")) {
        try {
          const zip = new JSZip();
          const zipContents = await zip.loadAsync(file);

          await Promise.all(
            Object.keys(zipContents.files).map(async (filename) => {
              const zipEntry = zipContents.files[filename];

              if (!zipEntry.dir) {
                // Get the content of the file inside the ZIP
                const fileData = await zipEntry.async("blob");

                newList.push(new File([fileData], filename));
              }
            })
          );

          // Update the file list after unzipping
        } catch (error) {
          console.error("Error unzipping file: ", error);
        }
      }

      newList = newList?.flatMap((fileRecord: any) => {
        if (!fileRecord.name.endsWith(".zip")) {
          const isListMax = fileRecord.size / 1024 / 1024 > FileConfig.maxSize;
          if (isListMax) {
            fileRecord.status = "error";
            fileRecord.response = t("US.COMMON:COMMON.FILE_SIZE_EXCEEDED");
          }

          const reader = new FileReader();
          // Define what happens once the file is read
          reader.onload = function (event: any) {
            const fileContent = event.target.result;

            // Parse the JSON content
            try {
              const jsonData = JSON.parse(fileContent);
              fileRecord.stateName = jsonData?.StateName;
              fileRecord.stateGroupName = jsonData?.StateGroupName;
            } catch (error) {
              console.error("Error parsing JSON: ", error);
            }
          };
          reader.readAsText(fileRecord);

          return fileRecord;
        } else {
          return [];
        }
      });
      updateFileList([...uploadedFiles, ...newList]);
    }
    return false;
  };

  /**
   * @description - Upload and import handler
   */
  const handleSubmit = async (
    type: UploadActionTypes,
    category: number,
    uploadedFiles: Array<any>
  ) => {
    if (uploadedFiles?.length > 0) {
      const saveParams = await ImportFiles.call({
        uploadedFiles,
        category,
        isStateWorkflowView,
        workflowCategories: workflowCategories.data,
        isEnableDBUpload: type == UploadActionTypes.IMPORT ? true : false,
      });
      const searchParams = WorkflowGroup.call(
        getEntityNumberAndType(
          params["type"],
          params["id"],
          caseId?.toString() ?? ""
        )
      );

      if (type == UploadActionTypes.UPLOAD) {
        importWorkflow &&
          importWorkflow({ saveParams, searchParams, isStateWorkflowView });
      } else {
        importWorkflow &&
          importWorkflow({ saveParams, searchParams, isStateWorkflowView });
      }
    }
  };

  /**
   * @description - Handle replace workflow
   */
  const handleReplaceWorkflow = (
    file: any,
    checked: boolean,
    selectedCategory: any
  ) => {
    if (Array.isArray(uploadedFiles)) {
      const newUploadedFiles = [...uploadedFiles];
      const toggledIndex = _.findIndex(newUploadedFiles, {
        stateName: file?.stateName,
      });
      if (toggledIndex > -1) {
        newUploadedFiles[toggledIndex].isEnableReplace = checked;
        updateFileList(newUploadedFiles);
        handleSubmit(
          UploadActionTypes.UPLOAD,
          selectedCategory,
          newUploadedFiles
        );
      }
    }
  };

  /**
   * @description - Custom record layout for selected files
   */
  const customItemRender = (file: any, selectedCategory: any, isValid: any) => {
    const fileName = file?.name?.replace(/\.[^/.]+$/, "");
    return (
      <$Row className="wfi-upload-item">
        <$Col span={2} className="wfi-icon-container">
          {_?.find(workflowStates.data, { stateCategoryId: selectedCategory })
            ?.stateCategoryName !== file?.stateGroupName &&
            isStateWorkflowView && (
              <$Popover
                placement="topLeft"
                content={t(
                  "US.COLLECTION.CASE:WORKFLOWS.THERE_IS_A_MISMATCH_BETWEEN_THE_CATEGORIES"
                )}
                trigger="hover"
              >
                <ExclamationCircleFilled className="text-warning" />
              </$Popover>
            )}
          <PaperClipOutlined className="text-muted ml-2" />
        </$Col>
        <$Col span={9} className="wfi-filename pr-3">
          <$Tooltip placement="topLeft" title={fileName}>
            {fileName}
          </$Tooltip>
        </$Col>
        <$Col span={12} className="wfi-error pr-3">
          <div
            className={`wfi-error-message ${
              file?.errorCode == 3 || file?.errorCode == 4
                ? "wfi-text-warning"
                : "text-error"
            }`}
          >
            {file?.response && (
              <$Tooltip
                placement="topLeft"
                title={t(`US.COLLECTION.CASE:WORKFLOWS.${file?.response}`)}
              >
                <small>
                  {t(`US.COLLECTION.CASE:WORKFLOWS.${file?.response}`)}
                </small>
              </$Tooltip>
            )}
          </div>
          <div className="pl-3">
            {(file?.errorCode == 3 || file?.errorCode == 4) && (
              <$Tooltip
                placement="topLeft"
                title={t(
                  `US.COLLECTION.CASE:WORKFLOWS.REPLACE_EXISTING_RECORD`
                )}
              >
                <div>
                  <$Switch
                    name={`isEnableReplace-${file?.stateName}`}
                    checked={file?.isEnableReplace}
                    disabled={isWorkflowsImporting || !isValid}
                    onChange={(checked: boolean) =>
                      handleReplaceWorkflow(file, checked, selectedCategory)
                    }
                  />
                </div>
              </$Tooltip>
            )}
          </div>
        </$Col>
        <$Col span={1} className="text-error">
          <$Popconfirm
            title={t("US.COLLECTION.CASE:WORKFLOWS.SURE_TO_DELETE_?")}
            data-testid="remove-pop-confirm"
            okText={t("US.COMMON:COMMON.YES")}
            cancelText={t("US.COMMON:COMMON.NO")}
            onConfirm={() => {
              const index = uploadedFiles.indexOf(file);
              uploadedFiles?.splice(index, 1);
              updateFileList([...uploadedFiles]);
              handleSubmit(
                UploadActionTypes.UPLOAD,
                selectedCategory,
                uploadedFiles
              );
            }}
            placement="right"
          >
            <$Tooltip placement="topLeft" title={t("US.COMMON:COMMON.DELETE")}>
              <DeleteOutlined
                data-testid="remove-icon"
                style={{
                  pointerEvents:
                    isWorkflowsImporting || !isValid ? "none" : "auto",
                  cursor:
                    isWorkflowsImporting || !isValid
                      ? "not-allowed"
                      : "pointer",
                }}
              />
            </$Tooltip>
          </$Popconfirm>
        </$Col>
      </$Row>
    );
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        stateType: StateType.MAIN,
        category: null,
      }}
      innerRef={formikRef}
      onSubmit={() => {}}
      validationSchema={
        isStateWorkflowView ? ImportWorkflowValidationSchema : undefined
      }
    >
      {({ values, setValues, isValid, setFieldValue }: any) => (
        <>
          <div className="wfi-container mb-4">
            {isStateWorkflowView && (
              <$Row gutter={16} className="mb-3">
                <$Col span={9}>
                  <label className="ant-form-item-required">
                    {t("US.COLLECTION.CASE:WORKFLOWS.STATE_TYPE")}
                  </label>
                  <$Select
                    name="stateType"
                    placeholder={t("US.COLLECTION.CASE:WORKFLOWS.STATE_TYPE")}
                    size="small"
                    options={[
                      {
                        label: t("US.COLLECTION.CASE:WORKFLOWS.MAIN_STATES"),
                        value: StateType.MAIN,
                      },
                      {
                        label: t(
                          "US.COLLECTION.CASE:WORKFLOWS.OPTIONAL_STATES"
                        ),
                        value: StateType.OPTIONAL,
                      },
                    ]}
                    allOption={false}
                    onSearchBy={["label"]}
                    onSelect={(value) => {
                      setValues((previousValues: any) => ({
                        ...previousValues,
                        category: null,
                        stateType: value,
                      }));
                    }}
                    className="w-100"
                    required
                    value={values.stateType}
                    formitem={{}}
                    disabled={isCategorySaveing || isWorkflowsImporting}
                  />
                </$Col>
                <$Col span={15}>
                  <label className="ant-form-item-required">
                    {t("US.COLLECTION.CASE:WORKFLOWS.CATEGORY")}
                  </label>
                  <div className="d-flex flex-fill">
                    <div className="w-100">
                      <$Select
                        name="category"
                        placeholder={t("US.COLLECTION.CASE:WORKFLOWS.CATEGORY")}
                        size="small"
                        options={getWorkflowCategoryOptions(
                          workflowStates.data,
                          values?.stateType
                        )}
                        onSelect={(value: number) => {
                          setFieldValue("category", value);
                          if (uploadedFiles?.length > 0)
                            handleSubmit(
                              UploadActionTypes.UPLOAD,
                              value,
                              uploadedFiles
                            );
                        }}
                        allOption={false}
                        autoFocus={true}
                        onSearchBy={["label"]}
                        className="w-100"
                        required
                        value={values.category}
                        formitem={{}}
                        disabled={isCategorySaveing || isWorkflowsImporting}
                      />
                    </div>
                    <$Button
                      icon={<PlusOutlined />}
                      size="small"
                      className="mt-1"
                      onClick={() =>
                        openDrawer &&
                        openDrawer({
                          ...workflowDrawerInitial,
                          title:
                            values.stateType == StateType.MAIN
                              ? "US.COLLECTION.CASE:WORKFLOWS.ADD_NEW_MAIN_STATE_CATEGORY"
                              : "US.COLLECTION.CASE:WORKFLOWS.ADD_NEW_OPTIONAL_STATE_CATEGORY",
                          isVisible: true,
                          type: WorkflowDrawerType.CATEGORY,
                          stateType: values.stateType,
                        })
                      }
                    />
                  </div>
                </$Col>
              </$Row>
            )}
            <Dragger
              name="fileUpload"
              data-testid="upload-dragger"
              accept={FileConfig.formats}
              multiple={true}
              fileList={uploadedFiles}
              disabled={
                !isValid ||
                isWorkflowsImporting ||
                isCategorySaveing ||
                workflowCategories.isLoading
              }
              beforeUpload={beforeUploadFile}
              itemRender={(originNode, file: any) =>
                customItemRender(file, values.category, isValid)
              }
              onChange={() => {
                handleSubmit(
                  UploadActionTypes.UPLOAD,
                  values?.category,
                  uploadedFiles
                );
              }}
            >
              <p className="ant-upload-drag-icon mb-0 mr-3">
                <UploadOutlined />
              </p>
              <div>
                <p className="ant-upload-text">
                  {t(
                    "US.COMMON:COMMON.CLICK_OR_DRAG_FILE_TO_THIS_AREA_TO_UPLOAD"
                  )}
                </p>
                <p className="ant-upload-hint">
                  {t("US.COMMON:COMMON.SUPPORT_FOR_MULTIPLE_FILE_UPLOAD")}
                </p>
                {workflowCategories.isLoading && (
                  <p className="font-sm text-muted">
                    {t("US.COMMON:COMMON.PLEASE_WAIT")}
                  </p>
                )}
              </div>
            </Dragger>
          </div>

          <div className="drawer-footer-fixed align-content-center justify-content-end">
            <div>
              <$Popconfirm
                title={t("US.COMMON:COMMON.DO_YOU_WISH_TO_CONTINUE_?")}
                placement="topLeft"
                onConfirm={() =>
                  handleSubmit(
                    UploadActionTypes.IMPORT,
                    values?.category,
                    uploadedFiles
                  )
                }
                okText={t("US.COMMON:COMMON.YES")}
                cancelText={t("US.COMMON:COMMON.NO")}
                disabled={isImportDisabled || !isValid}
              >
                <$Button
                  className="ml-3 mr-2"
                  data-testid="import-btn"
                  type="primary"
                  disabled={isImportDisabled || !isValid}
                  loading={isWorkflowsImporting}
                >
                  {t("US.COLLECTION.COMMON:COMMON.IMPORT")}
                </$Button>
              </$Popconfirm>
              <$Button
                className="mr-2"
                onClick={() => {
                  resetImport && resetImport({});
                  setValues((previousValues: any) => ({
                    ...previousValues,
                    category: null,
                    stateType: StateType.MAIN,
                  }));
                }}
                disabled={isWorkflowsImporting}
              >
                {t("US.COLLECTION.COMMON:COMMON.RESET")}
              </$Button>
              <$Popconfirm
                title={t("US.COLLECTION.CASE:WORKFLOWS.CANCEL_ERROR")}
                placement="topLeft"
                onConfirm={() => onClose()}
                okText={t("US.COMMON:COMMON.YES")}
                cancelText={t("US.COMMON:COMMON.NO")}
                disabled={isWorkflowsImporting}
              >
                <$Button disabled={isWorkflowsImporting}>
                  {t("US.COLLECTION.COMMON:COMMON.CANCEL")}
                </$Button>
              </$Popconfirm>
            </div>
          </div>
        </>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { Workflow, domainView } = state;
  const { metaData } = domainView;
  const {
    importDrawer,
    uploadedResult,
    uploadedFiles,
    workflowStates,
    newCategoryId,
    isCategorySaveing,
    workflowCategories,
    isWorkflowsImporting,
  } = Workflow;
  return {
    metaData,
    importDrawer,
    uploadedResult,
    uploadedFiles,
    workflowStates,
    newCategoryId,
    workflowCategories,
    isCategorySaveing,
    isWorkflowsImporting,
  };
};

const { category, categories, importWorkflow } = workflowAction;

const mapDispatchToProps = {
  resetImport: importWorkflow.reset,
  updateFiles: importWorkflow.update,
  importWorkflow: importWorkflow.save,
  openDrawer: category.openDrawer,
  getCategories: categories.get,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ImportWorkflow);
